import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '@admin-core/services/auth.service';
import { UserService } from '@admin-core/services/user.service';
import { AuthUser, UserRole } from '@admin-core/models/auth.model';
import { LanguageService } from '@admin-core/i18n/language.service';
import { TranslationKey } from '@admin-core/i18n/translations/en';
import { TranslatePipe } from '@admin-core/i18n/translate.pipe';
import { ConfirmDialogService } from '@admin-shared/components/confirm-dialog/confirm-dialog.service';

@Component({
  selector: 'app-users-admin',
  standalone: true,
  imports: [ReactiveFormsModule, TranslatePipe],
  templateUrl: './users-admin.component.html',
  styleUrl: './users-admin.component.scss'
})
export class UsersAdminComponent implements OnInit {
  private readonly api = inject(UserService);
  private readonly auth = inject(AuthService);
  private readonly fb = inject(FormBuilder);
  private readonly i18n = inject(LanguageService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly items = signal<AuthUser[]>([]);
  readonly loading = signal(false);
  readonly dialogOpen = signal(false);
  readonly editingId = signal<string | null>(null);

  readonly actor = this.auth.user;
  readonly isPortalAdmin = () => this.actor()?.role === 'PortalAdmin';
  readonly assignableRoles = (): UserRole[] =>
    this.isPortalAdmin() ? ['PortalAdmin', 'BusinessAdmin'] : ['BusinessAdmin'];

  readonly form = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email, Validators.maxLength(320)]],
    password: [''],
    name: ['', [Validators.required, Validators.maxLength(200)]],
    role: this.fb.nonNullable.control<UserRole>('BusinessAdmin'),
    businessIds: this.fb.nonNullable.control<string[]>([]),
    isActive: [true]
  });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api.getAll().subscribe({
      next: (items) => {
        this.items.set(items);
        this.loading.set(false);
      },
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('users.loadError'));
        this.loading.set(false);
      }
    });
  }

  canEdit(user: AuthUser): boolean {
    const actor = this.actor();
    if (!actor) {
      return false;
    }
    if (actor.role === 'PortalAdmin') {
      return true;
    }
    if (actor.role === 'BusinessAdmin') {
      return user.role === 'BusinessAdmin' || user.id === actor.id;
    }
    return false;
  }

  canImpersonate(user: AuthUser): boolean {
    const actor = this.actor();
    if (!actor || actor.id === user.id || !user.isActive || this.auth.isImpersonating()) {
      return false;
    }
    if (user.role === 'PortalAdmin') {
      return false;
    }
    if (actor.role === 'PortalAdmin') {
      return true;
    }
    return actor.role === 'BusinessAdmin' && user.role === 'BusinessAdmin';
  }

  impersonate(user: AuthUser): void {
    if (!this.canImpersonate(user)) {
      return;
    }

    this.auth.impersonate(user.id).subscribe({
      next: () => {
        window.location.href = '/admin';
      },
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('users.impersonateError'));
      }
    });
  }

  openCreate(): void {
    this.editingId.set(null);
    this.form.reset({
      email: '',
      password: '',
      name: '',
      role: 'BusinessAdmin',
      businessIds: [],
      isActive: true
    });
    this.form.controls.email.enable();
    this.form.controls.role.enable();
    this.form.controls.businessIds.enable();
    this.form.controls.password.setValidators([Validators.required, Validators.minLength(8)]);
    this.form.controls.password.updateValueAndValidity();
    this.dialogOpen.set(true);
  }

  openEdit(user: AuthUser): void {
    this.editingId.set(user.id);
    this.form.reset({
      email: user.email,
      password: '',
      name: user.name,
      role: user.role,
      businessIds: (user.businesses ?? []).map((b) => b.id),
      isActive: user.isActive
    });
    this.form.controls.email.disable();
    this.form.controls.password.setValidators([Validators.minLength(8)]);
    this.form.controls.password.updateValueAndValidity();

    const selfEdit = this.actor()?.role === 'BusinessAdmin' && user.id === this.actor()?.id;
    if (selfEdit) {
      this.form.controls.role.disable();
      this.form.controls.businessIds.disable();
    } else {
      this.form.controls.role.enable();
      this.form.controls.businessIds.enable();
    }

    this.dialogOpen.set(true);
  }

  closeDialog(): void {
    this.dialogOpen.set(false);
    this.form.controls.email.enable();
    this.form.controls.role.enable();
    this.form.controls.businessIds.enable();
  }

  selectedRole(): UserRole {
    return this.form.controls.role.value;
  }

  toggleBusiness(id: string, checked: boolean): void {
    const current = [...this.form.controls.businessIds.value];
    if (checked && !current.includes(id)) {
      current.push(id);
    } else if (!checked) {
      const idx = current.indexOf(id);
      if (idx >= 0) {
        current.splice(idx, 1);
      }
    }
    this.form.controls.businessIds.setValue(current);
  }

  isBusinessSelected(id: string): boolean {
    return this.form.controls.businessIds.value.includes(id);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const password = raw.password.trim();
    if (password && password.length < 8) {
      this.form.controls.password.setErrors({ minlength: true });
      this.form.markAllAsTouched();
      return;
    }

    const selfEdit =
      !!this.editingId() &&
      this.actor()?.role === 'BusinessAdmin' &&
      this.editingId() === this.actor()?.id;

    const role = selfEdit
      ? (this.actor()?.role as UserRole)
      : this.isPortalAdmin()
        ? raw.role
        : 'BusinessAdmin';

    const businessIds = role === 'BusinessAdmin' ? raw.businessIds : [];
    if (role === 'BusinessAdmin' && businessIds.length === 0) {
      this.showError(this.i18n.t('users.selectBusiness'));
      return;
    }

    const id = this.editingId();
    const base = {
      name: raw.name.trim(),
      role,
      businessId: businessIds[0] ?? null,
      businessIds,
      isActive: raw.isActive
    };

    const req$ = id
      ? this.api.update(id, {
          ...base,
          password: password || null
        })
      : this.api.create({
          ...base,
          email: raw.email.trim(),
          password
        });

    req$.subscribe({
      next: () => {
        this.closeDialog();
        this.load();
      },
      error: (err) => {
        const apiError =
          err?.error?.error ??
          (typeof err?.error?.title === 'string' ? err.error.title : null) ??
          this.i18n.t('users.saveError');
        this.showError(apiError);
      }
    });
  }

  roleKey(role: UserRole): TranslationKey {
    if (role === 'BusinessAdmin') {
      return 'users.role.BusinessAdmin';
    }
    return 'users.role.PortalAdmin';
  }

  scopeLabel(user: AuthUser): string {
    if (user.role !== 'BusinessAdmin') {
      return '—';
    }
    const names = (user.businesses ?? []).map((b) => b.name);
    if (names.length > 0) {
      return names.join(', ');
    }
    return user.businessName ?? user.businessKey ?? '';
  }

  private showError(message: string): void {
    void this.confirmDialog.alert({
      title: this.i18n.t('common.errorTitle'),
      message,
      danger: true
    });
  }
}
